/**
 * CF-BONUS Ticket B — transport for `GET /api/v1/bonus`.
 *
 * Thin by design: every contract decision lives in `bonus-state.ts`. This module only turns a
 * network outcome into a `BonusView`, so there is exactly ONE place that can decide what a
 * failure means (Scorer amendment A1: a failure is `unavailable`, never `none`).
 *
 * Deliberately NOT a polling loop (his accepted constraint): callers refresh on mount and on
 * the four settlement events. The endpoint allows 60/user/min; event-driven refresh sits far
 * below that.
 */

import { apiFetch } from './api';
import {
  NETWORK_UNAVAILABLE, bonusViewFromStatus, parseBonusBody, type BonusView,
} from './bonus-state';
import type { ReadObservation } from './bonus-retry-policy.ts';
import type { ReadOutcome } from './bonus-state';

/** The refresh triggers Scorer enumerated. Used for telemetry/debug labelling only. */
export type BonusRefreshReason =
  | 'mount'
  | 'session_ready'          // CF-BONUS-FLASH-FIX-V1: the mount read races auth and 401s; this
                             // event-driven re-read fires when the session first loads. Still
                             // not a polling loop — it is the 6th discrete state edge.
  | 'deposit_credited'
  | 'flip_settled'
  | 'withdrawal_queued'
  | 'withdrawal_completed'
  | 'failed_flip_refunded';

/**
 * Fetch the caller's bonus projection. Never throws — every failure path resolves to an
 * `unavailable` view so a caller cannot accidentally render "no bonus" because of an error.
 *
 * No caller-supplied identity: the backend derives it from the Telegram auth header that
 * `apiFetch` attaches. There is deliberately no parameter for a user id.
 */
/**
 * CF-BONUS-SETTLED-UNAVAILABLE-RESILIENCE-V1 — OWNER-ONLY read.
 *
 * Identical network behaviour to `fetchBonusState` (single attempt, no delay, no retry, no backoff),
 * but it also returns the raw OBSERVATION so `bonus-retry-policy.ts` can tell a transient blip from
 * a throttle from a permanent fault. `fetchBonusState` collapses every failure to a view and
 * therefore cannot classify — that is why this exists rather than a flag on the old function.
 *
 * ⛔ Only the coordinator may call this. `WithdrawModal` stays bound to `fetchBonusState`.
 *
 * ⛔ DELIBERATE DUPLICATION: `fetchBonusState` below is left byte-identical rather than refactored
 * to delegate here. Delegating would preserve semantics in principle, but it is the fail-closed
 * barrier in front of a withdrawal and there is no installed transport test that could PROVE the
 * refactor equivalent — the retained suite covers the parser, not this seam. Fifteen duplicated
 * lines are cheaper than an unprovable change to a money path.
 */
export async function fetchBonusOutcome(
  apiBase: string,
): Promise<ReadOutcome> {
  let resp: Response;
  try {
    resp = await apiFetch(`${apiBase}/api/v1/bonus`, { method: 'GET' });
  } catch {
    return { ok: false, observation: { kind: 'network' }, view: NETWORK_UNAVAILABLE };
  }

  if (!resp.ok) {
    return { ok: false, observation: { kind: 'http', status: resp.status }, view: bonusViewFromStatus(resp.status) };
  }

  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    return { ok: false, observation: { kind: 'malformed_body' }, view: { kind: 'unavailable', reason: 'contract' } };
  }

  const view = parseBonusBody(body);
  // A 200 that parses to `unavailable` failed the v1 contract (A4). That is a permanent contract
  // fault, not a successful read of "no bonus" — conflating them would be exactly the A1 mistake.
  if (view.kind === 'unavailable') return { ok: false, observation: { kind: 'malformed_body' }, view };
  return { ok: true, observation: { kind: 'ok' }, view };
}

export async function fetchBonusState(apiBase: string): Promise<BonusView> {
  let resp: Response;
  try {
    resp = await apiFetch(`${apiBase}/api/v1/bonus`, { method: 'GET' });
  } catch {
    return NETWORK_UNAVAILABLE;
  }

  if (!resp.ok) {
    // 401 → auth; anything else non-2xx → http. Both are `unavailable`, never `none`.
    return bonusViewFromStatus(resp.status);
  }

  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    // A 200 whose body will not parse is a contract failure, not an absence of bonus.
    return { kind: 'unavailable', reason: 'contract' };
  }

  return parseBonusBody(body);
}
